import {
  IonButton,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonText
} from "@ionic/react";
import {
  addCircleOutline,
  chevronDownOutline,
  chevronForwardOutline,
  refreshOutline,
  trashOutline
} from "ionicons/icons";
import { useRef, useState } from "react";
import { validatePotName } from "../logic/potNameRules";
import type { SpendingPot } from "../types";

interface PotSelectorScreenProps {
  isAuthenticated: boolean;
  restoreErrorMessage: string | null;
  selectedPotId: string;
  spendingPots: SpendingPot[];
  statusMessage: string | null;
  onArchivePot: (potId: string) => Promise<void>;
  onCreatePot: (name: string) => Promise<void>;
  onReturn: () => void;
  onRestorePot: (potId: string) => Promise<void>;
  onSelectPot: (potId: string) => void;
}

export function PotSelectorScreen({
  isAuthenticated,
  restoreErrorMessage,
  selectedPotId,
  spendingPots,
  statusMessage,
  onArchivePot,
  onCreatePot,
  onReturn,
  onRestorePot,
  onSelectPot
}: PotSelectorScreenProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [potName, setPotName] = useState("");
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [archiveCandidate, setArchiveCandidate] = useState<SpendingPot | null>(null);
  const [showArchivedPots, setShowArchivedPots] = useState(false);
  const potNameInputRef = useRef<HTMLIonInputElement | null>(null);
  const activePots = spendingPots.filter((pot) => !pot.isArchived);
  const archivedPots = spendingPots.filter((pot) => pot.isArchived);

  function beginCreatingPot() {
    setIsCreating(true);
    window.setTimeout(() => {
      void potNameInputRef.current?.setFocus();
    }, 80);
  }

  async function submitPot() {
    const trimmedName = potName.trim();

    if (!trimmedName) {
      setValidationMessage(validatePotName(trimmedName, spendingPots));
      return;
    }

    const validation = validatePotName(trimmedName, activePots);
    if (validation) {
      setValidationMessage(validation);
      return;
    }

    setValidationMessage(null);
    setIsSubmitting(true);

    try {
      await onCreatePot(trimmedName);
      setPotName("");
      setIsCreating(false);
    } catch (error) {
      setValidationMessage(error instanceof Error ? error.message : "Could not create pot.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="tracker-screen pot-screen">
      <section className="pot-panel">
        <div className="pot-screen-heading">
          <div>
            <p>Spending pots</p>
            <h1>Select pot</h1>
          </div>
          <IonButton fill="clear" size="small" onClick={onReturn}>
            Done
          </IonButton>
        </div>

        {!isAuthenticated ? (
          <div className="empty-state">
            <strong>Sign in to use spending pots</strong>
            <span>Anonymous tracking will keep working locally on this browser.</span>
          </div>
        ) : (
          <>
            {!isCreating ? (
              <IonButton expand="block" size="large" onClick={beginCreatingPot}>
                <IonIcon slot="start" icon={addCircleOutline} />
                Create Pot
              </IonButton>
            ) : (
              <section className="pot-create-panel">
                <IonItem lines="none" className="field-item">
                  <IonLabel position="stacked">Pot Name</IonLabel>
                  <IonInput
                    ref={potNameInputRef}
                    autocomplete="off"
                    placeholder="Enter pot name"
                    value={potName}
                    onIonInput={(event) => {
                      const nextValue = event.detail.value ?? "";
                      setPotName(nextValue);
                      setValidationMessage(validatePotName(nextValue, activePots));
                    }}
                  />
                </IonItem>
                {validationMessage ? (
                  <IonText color="danger">
                    <p className="validation-message">{validationMessage}</p>
                  </IonText>
                ) : null}
                <IonButton
                  expand="block"
                  size="large"
                  disabled={isSubmitting || Boolean(validatePotName(potName, activePots))}
                  onClick={submitPot}
                >
                  Create Pot
                </IonButton>
                <IonButton fill="clear" size="small" disabled={isSubmitting} onClick={() => setIsCreating(false)}>
                  Cancel
                </IonButton>
              </section>
            )}

            <div className="pot-divider" role="separator" />

            {statusMessage ? <p className="pot-status">{statusMessage}</p> : null}

            {activePots.length === 0 ? (
              <div className="empty-state">
                <strong>No pots yet</strong>
                <span>Start tracking and a Default pot will be created automatically.</span>
              </div>
            ) : (
              <div className="pot-list" aria-label="Spending pot list">
                {activePots.map((pot) => (
                  <div className={`pot-row ${pot.id === selectedPotId ? "selected" : ""}`} key={pot.id}>
                    <button className="pot-list-item" type="button" onClick={() => onSelectPot(pot.id)}>
                      <span>{pot.name}</span>
                      <small>{pot.subtitle}{pot.isDefault ? " • Default" : ""}</small>
                    </button>
                    {pot.id !== selectedPotId ? (
                      <IonButton
                        className="pot-row-action"
                        fill="clear"
                        size="small"
                        aria-label={`Archive ${pot.name}`}
                        onClick={() => setArchiveCandidate(pot)}
                      >
                        <IonIcon slot="icon-only" icon={trashOutline} />
                      </IonButton>
                    ) : null}
                  </div>
                ))}
              </div>
            )}

            <section className="archived-pots-panel">
              <button
                className="archived-pots-toggle"
                type="button"
                onClick={() => setShowArchivedPots((current) => !current)}
              >
                <IonIcon icon={showArchivedPots ? chevronDownOutline : chevronForwardOutline} />
                <span>Archived pots</span>
                <strong>{archivedPots.length}</strong>
              </button>

              {showArchivedPots ? (
                archivedPots.length === 0 ? (
                  <div className="empty-state compact-empty-state">
                    <strong>No archived pots</strong>
                    <span>Archived pots will appear here.</span>
                  </div>
                ) : (
                  <div className="pot-list archived-pot-list" aria-label="Archived spending pot list">
                    {restoreErrorMessage ? <p className="validation-message">{restoreErrorMessage}</p> : null}
                    {archivedPots.map((pot) => (
                      <div className="pot-row archived" key={pot.id}>
                        <div className="pot-list-item archived-item">
                          <span>{pot.name}</span>
                          <small>{pot.subtitle}{pot.isDefault ? " • Default" : ""}</small>
                          <small className="archived-at">{formatArchivedAt(pot.archivedAtUtc)}</small>
                        </div>
                        <IonButton
                          className="pot-row-action"
                          fill="clear"
                          size="small"
                          aria-label={`Restore ${pot.name}`}
                          onClick={() => void onRestorePot(pot.id)}
                        >
                          <IonIcon slot="icon-only" icon={refreshOutline} />
                        </IonButton>
                      </div>
                    ))}
                  </div>
                )
              ) : null}
            </section>
          </>
        )}
      </section>

      <IonModal className="archive-confirmation-modal" isOpen={archiveCandidate !== null} onDidDismiss={() => setArchiveCandidate(null)}>
        <div className="archive-modal">
          <h2>Archive pot?</h2>
          <p>
            You will no longer be able to track spend on this pot and any shared users of this pot will be un-linked.
          </p>
          <div className="archive-modal-actions">
            <IonButton fill="outline" onClick={() => setArchiveCandidate(null)}>
              Cancel
            </IonButton>
            <IonButton
              color="danger"
              onClick={() => {
                if (!archiveCandidate) {
                  return;
                }

                void onArchivePot(archiveCandidate.id);
                setArchiveCandidate(null);
              }}
            >
              Archive
            </IonButton>
          </div>
        </div>
      </IonModal>
    </main>
  );
}

function formatArchivedAt(archivedAtUtc: string | null) {
  if (!archivedAtUtc) {
    return "Archived date unavailable";
  }

  return `Archived ${new Date(archivedAtUtc).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short"
  })}`;
}
